<template>
  <div v-if="host !== null">
    <h3>Photos</h3>
    <drop-zone
      v-model="host.photoUrlEstate"
      :class="{ 'ml-4': $mq === 'lg' }"
      :edit-mode="editMode"
    ></drop-zone>
    <h3>Informations</h3>
    <v-list>
      <template v-for="item in infos">
        <FormLine
          :item="item"
          v-model="host"
          :key="item.name"
          :edit-mode="editMode"
        />
        <v-divider :key="`${item.name}-divider`" />
      </template>
    </v-list>
    <h3>Description</h3>
    <div style="word-break: break-word" class="ml-3 mr-3 mt-2">
      <div v-if="!editMode">{{ host.description || "Pas de description" }}</div>
      <v-textarea
        v-else
        v-model="host.description"
        style="width: 100%"
      ></v-textarea>
    </div>
    <h3>Contact</h3>
    <v-list>
      <FormLine
        v-for="item in contact"
        :item="item"
        v-model="host"
        :key="item.name"
        :edit-mode="editMode"
      ></FormLine>
    </v-list>
    <v-snackbar v-model="showError" :timeout="7000"
      >Une erreur a eu lieu</v-snackbar
    >
  </div>
</template>

<script>
import { createSyncHandler } from "@/stores/createSyncHandler";
import FormLine from "../commons/FormLine";
import DropZone from "../commons/DropZone";
import { fetchHil, objectIdToDate } from "@/utils";
import * as _ from "lodash";

export default {
  name: "HostDetails",
  props: ["id", "editMode"],
  components: {
    FormLine,
    DropZone
  },
  data() {
    return {
      host: null,
      hostOrig: null,
      showError: false,
      infos: [
        {
          name: "address",
          label: "Adresse",
          validation: "required",
          as: "geocomplete",
          onAddressChanged: async (newAddress, format) => {
            if (!newAddress) {
              this.host.addressObject = null;
              return;
            }
            this.host.addressObject = newAddress;
            const addrResp = await fetch(
              "https://api-adresse.data.gouv.fr/search/?limit=10&q=" +
                encodeURIComponent(format.formatted_address)
            );
            if (!addrResp.ok) {
              throw new Error("geoloc has failed");
            }
            const data = await addrResp.json();
            if (data.features && data.features.length > 0) {
              this.host.geoLoc = data.features[0].geometry;
            }
          },
          addressIsValidAtBeginning: () => this.host.addressObject != null
        },
        {
          name: "externalId",
          label: "Lien",
          validation: "required|url",
          as: "link"
        },
        {
          name: "carrez",
          label: "Surface",
          validation: "numeric",
          as: "number",
          suffix: "m²"
        },
        {
          name: "numberOfRoom",
          label: "Pièces",
          validation: "integer|required",
          as: "number",
          suffix: "pièce(s)"
        },
        { name: "price", label: "Prix", validation: "numeric", suffix: "€" },
        {
          name: "addDate",
          label: "Date d'ajout",
          readOnly: true,
          value: () => this.$options.filters.date(objectIdToDate(this.id))
        },
        {
          name: "dateAvailable",
          label: "Date de disponibilité",
          as: "date"
        },
        {
          name: "coordinates",
          label: "Coordonnées",
          readOnly: true,
          value: () => {
            const host = this.host;
            return host.geoLoc && host.geoLoc.coordinates
              ? host.geoLoc.coordinates
              : "??";
          }
        }
      ],
      contact: [
        { name: "contactName", label: "Nom" },
        { name: "contactPhoneNumber", label: "Téléphone" },
        { name: "contactEmail", label: "Mail", validation: "email" }
      ]
    };
  },
  async mounted() {
    this.reload();
  },
  timers: {
    sync: { time: 2000, autostart: true, repeat: true }
  },
  methods: {
    async reload() {
      let id = this.id;
      const resp = await (await fetchHil(`/hosts/${id}`)).json();
      this.setHost(resp);
    },

    setHost(host) {
      const resp = _.omit(host, ["__v", "userId"]);
      this.host = resp;
      this.hostOrig = _.cloneDeep(resp);
    },

    // Reuse sync handler outside the context of a store, it will receive injected store equivalents
    syncHandler: createSyncHandler(
      state => `/hosts/${state.current._id}`,
      null,
      ["_id", "__v", "preciseAddress", "preciseAddressObject"] // deprecated preciseAddress should not be sent
    ),

    async sync() {
      if (!(await this.$validator.validate())) {
        return;
      }

      try {
        await this.syncHandler({
          state: { previous: this.hostOrig, current: this.host },
          commit: (ignored, { resp }) => {
            this.setHost(resp);
            this.$emit("reload", _.cloneDeep(this.host));
          }
        });
      } catch (e) {
        this.showError = true;
      }
    }
  }
};
</script>
